import { MinusIcon, PlusIcon } from 'lucide-react'
import { UseFormReturn, useFieldArray } from 'react-hook-form'
import { z } from 'zod'

import { timingEvent } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { FormLabel } from '@/components/form'
import FormSelect from '@/components/form/formSelect'
import FormTimeInput from '@/components/form/formTimeInput'
import IconButton from '@/components/iconButton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/select'
import TimeInput from '@/components/timeInput'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Down, Init, ...timingEventWithoutInitDown } = timingEvent

export const up = '05:00'
export const down = '22:30'

const timingConfigSchema = z
    .object({
        type: z.nativeEnum(timingEventWithoutInitDown, {
            errorMap: () => ({ message: 'Invalid event type' }),
        }),
        value: z.string().length(5, 'Invalid time'),
    })
    .optional() // Optional due to Epemeral markets

// eslint-disable-next-line react-refresh/only-export-components
export const timingSchema = z.object({
    timingConfigs: z.array(timingConfigSchema),
})

type TimingSchemaType = z.infer<typeof timingSchema>

interface Props {
    form: UseFormReturn<TimingSchemaType>
}

export default function CreateMarketTimingForm({ form }: Props) {
    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'timingConfigs',
    })

    return (
        <>
            <FormLabel>Timing events</FormLabel>
            <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                    <Select disabled value="timing_init">
                        <SelectTrigger className="col-span-3">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="timing_init">Market Up</SelectItem>
                        </SelectContent>
                    </Select>
                    <TimeInput disabled className={'w-32'} value={up} />
                </div>

                <div className="grid grid-cols-4 gap-4">
                    <Select disabled value="timing_down">
                        <SelectTrigger className="col-span-3">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="timing_down">Market Down</SelectItem>
                        </SelectContent>
                    </Select>
                    <TimeInput disabled className={'w-32'} value={down} />
                </div>

                {fields.map((item, index) => (
                    <div key={item.id} className="grid grid-cols-12 gap-4">
                        <FormSelect
                            className="col-span-8"
                            control={form.control}
                            name={`timingConfigs.${index}.type`}
                            placeholder="Select an event"
                            options={Object.values(timingEventWithoutInitDown).map(e => ({
                                label: e,
                                value: e,
                            }))}
                        />
                        <div className="flex items-start space-x-4">
                            <FormTimeInput control={form.control} name={`timingConfigs.${index}.value`} />
                            <IconButton
                                variant="ghost"
                                tooltip="Remove"
                                className="mt-1 hover:text-destructive"
                                onClick={() => remove(index)}
                                icon={MinusIcon}
                            />
                        </div>
                    </div>
                ))}

                <Button variant="ghost" onClick={() => append({ type: 'None', value: '' })}>
                    <PlusIcon className="mr-2 h-3 w-3" />
                    Add event
                </Button>
            </div>
        </>
    )
}
